import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  Injector,
  computed,
  forwardRef,
  inject,
  input,
  numberAttribute,
  output,
} from '@angular/core';
import { NgClass } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { BaseControlValueAccessor } from '../base-control-value-accessor';

type InputType = 'number' | 'text' | 'money' | 'phone' | 'password';

@Component({
  selector: 'lib-form-text',
  templateUrl: './form-text.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatInputModule, MatFormFieldModule, NgxMaskDirective, NgClass],
  providers: [
    provideNgxMask(),
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => FormTextComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => FormTextComponent), multi: true },
  ],
})
export class FormTextComponent
  extends BaseControlValueAccessor<string>
  implements AfterContentInit, ControlValueAccessor, Validator
{
  private static nextId = 0;
  private injector = inject(Injector);

  readonly inputId = `form-text-${FormTextComponent.nextId++}`;
  label = input<string>('');
  hideRequiredMarker = input<boolean>(false);
  type = input<InputType>('text');
  // ngx-mask pattern: 0 = required digit, 9 = optional digit. Zip is "99999", percentage "999".
  mask = input<string | null>(null);
  startHint = input<string>();
  endHint = input<string>();
  prefix = input<string>();
  step = input<string>();
  min = input<string>();
  max = input<string>();
  autocomplete = input<string>('off');
  dropSpecialCharacters = input<boolean>(false);
  minError = input<string>();
  maxError = input<string>();
  pattern = input<string | RegExp>('');
  patternErrorMessage = input<string>();
  readonly = input<boolean>(false);
  multiline = input<boolean>(false);
  rows = input<number>(4);
  alignment = input<'left' | 'center' | 'right'>('left');
  placeholder = input<string>('');
  ariaLabel = input<string>('');
  maxlength = input<number | null, unknown>(null, { transform: numberAttribute });
  customErrors = input<Record<string, string>>({});

  blur = output<void>();

  // Chrome ignores autocomplete="off" on name/email-ish fields; a per-instance token it can't classify does work.
  autocompleteComputed = computed<string>(() =>
    this.autocomplete() === 'off' ? this.inputId : this.autocomplete(),
  );
  prefixComputed = computed<string>(() => this.prefix() ?? (this.type() === 'money' ? '$' : ''));
  stepComputed = computed<string | undefined>(
    () => this.step() ?? (this.type() === 'money' ? '0.01' : undefined),
  );
  minComputed = computed<string | undefined>(
    () => this.min() ?? (this.type() === 'money' ? '0' : undefined),
  );
  patternComputed = computed<string | RegExp>(() => {
    if (this.pattern()) return this.pattern();
    return this.type() === 'phone' ? /^\(\d{3}\) \d{3}-\d{4}$/ : '';
  });
  patternErrorMessageComputed = computed<string>(() => {
    const custom = this.patternErrorMessage();
    if (custom) return custom;
    switch (this.type()) {
      case 'money':
        return 'Cannot be more than two decimal places';
      case 'phone':
        return 'Must be a valid phone number like (123) 456-7890';
      default:
        return '';
    }
  });
  maskComputed = computed<string | null>(() => {
    const mask = this.mask();
    if (mask) return mask;
    switch (this.type()) {
      case 'phone':
        return '(999) 999-9999';
      case 'money':
        return 'separator.2';
      default:
        return null;
    }
  });

  ngAfterContentInit(): void {
    const ngControl = this.injector.get(NgControl, null);
    // Always have a control so the template @if never destroys the component.
    this.control = (ngControl?.control as FormControl) ?? new FormControl();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return control.valid ? null : control.errors;
  }

  get inputType(): 'number' | 'text' | 'password' {
    switch (this.type()) {
      case 'number':
        return 'number';
      case 'password':
        return 'password';
      default:
        return 'text';
    }
  }

  protected override getLabel(): string {
    return this.label() || '';
  }

  override getErrorMessage(errorKey: string): string {
    const custom = this.customErrors()[errorKey];
    if (custom) return custom;

    switch (errorKey) {
      case 'pattern':
        return this.patternErrorMessageComputed() || 'Invalid format';
      case 'min':
        return this.minError() || 'Value is below minimum';
      case 'max':
        return this.maxError() || 'Value exceeds maximum';
      case 'equalTo':
        return this.label().toLowerCase().includes('email')
          ? 'Emails do not match'
          : 'Values do not match';
      default:
        return super.getErrorMessage(errorKey);
    }
  }
}
